import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { save } from "../systems/Save";
import { quests } from "../systems/Quests";
import { QUEST_LEVELS, QUEST_DEFS, isQuestLevelComplete, isQuestLevelUnlocked, questProgress, type QuestDef } from "../systems/questDefs";
import { THEME, button } from "../ui/theme";
import { addCastleBackdrop, addContentPanel, addMenuBackdrop, addMenuHeader, addQuitButton, beginSceneLifecycle } from "../ui/menuUi";

const CARD_H = 100;
const CARD_GAP = 10;
const CARD_STRIDE = CARD_H + CARD_GAP;
/** Level heading row above each level's block of cards. */
const HEADING_H = 34;

// The body is split into two columns: this sidebar and the main content next
// to it. The sidebar sits inset from the content panel edges by BODY_INSET on
// every side, and BODY_GUTTER separates it from the main column that starts at
// SIDEBAR_X + SIDEBAR_W + BODY_GUTTER.
const BODY_INSET = 16;
const BODY_GUTTER = 14;
const SIDEBAR_X = 24 + BODY_INSET;
const SIDEBAR_Y = 96 + BODY_INSET;
const SIDEBAR_W = 320;
const SIDEBAR_H = GAME_HEIGHT - 116 - BODY_INSET * 2;

// Main column: everything right of the sidebar, sharing the same insets.
const CARD_X = SIDEBAR_X + SIDEBAR_W + BODY_GUTTER;
const CARD_W = GAME_WIDTH - 24 - BODY_INSET - CARD_X;

// Scrollable quest list: below the completion counter, above the footer buttons.
const LIST_TOP = 150;
const LIST_H = GAME_HEIGHT - 92 - LIST_TOP;

// Card body splits into 3 content-sized columns: description, rewards, and
// state — state is a fixed-width band at the card's right edge, rewards
// sits left of it, and description text wraps before the rewards column
// starts. Description is sized to 6/9 (two thirds) of the card width;
// rewards and state split the remaining third.
const STATE_COL_W = 122;
const REWARD_COL_W = 131;
const COL_GAP = 18;
const STATE_COL_X = CARD_X + CARD_W - STATE_COL_W;
const REWARD_COL_X = STATE_COL_X - COL_GAP - REWARD_COL_W;
const DESC_RIGHT = REWARD_COL_X - COL_GAP;

// medieval-border.png: 2048×512 gold frame shared with the achievements page.
// corner = width of the unstretched corner-ornament columns for nine-slicing.
const BORDER = { w: 2048, h: 512, corner: 110 };

// flag-*.png: 1024×1046 banner art. The metal name plate runs across the top
// and the cloth field hangs below it — both expressed as fractions of the
// source so the "LEVEL n" label and the state icon land in the right places.
const FLAG = { w: 1024, h: 1046 };
const FLAG_H = 112;
const FLAG_W = FLAG_H * (FLAG.w / FLAG.h);
const FLAG_PLATE_CY = 0.175;  // name-plate center, fraction of flag height
const FLAG_ICON_CY = 0.56;    // cloth-field center, fraction of flag height
const FLAG_ICON_SIZE = FLAG_W * 0.40;
// The banner art's silhouette isn't centered in its own canvas (more padding
// on the right than the left), so anchor on its true visual center instead
// of 0.5 — otherwise the plate label, cloth icon, and trail all sit a few
// pixels right of where the painted banner actually is.
const FLAG_ORIGIN_X = 467 / FLAG.w;

/** Nine-sliced gold frame sized to an arbitrary rect, so the corner ornaments
 * keep their shape while the rails stretch. Mirrors the achievement cards. */
function medievalBorder(
  scene: Phaser.Scene, cx: number, cy: number, width: number, height: number,
): Phaser.GameObjects.NineSlice {
  const scale = height / BORDER.h;
  return scene.add.nineslice(
    cx, cy, "frame-medieval-border", undefined,
    width / scale, BORDER.h,
    BORDER.corner, BORDER.corner, BORDER.corner, BORDER.corner,
  ).setScale(scale);
}

/** Fits an image inside a square box without stretching it. */
function fitIcon(image: Phaser.GameObjects.Image, box: number): Phaser.GameObjects.Image {
  const scale = box / Math.max(image.width, image.height);
  return image.setScale(scale);
}

// Reward/state icon-*.png visible pixel bounds — same technique as
// AchievementsScene's SHIELD_SRC/COIN_SRC. Each source PNG carries a
// different amount of transparent padding (the state-ring art has a
// decorative circular frame baked in; lock.png has none), so fitting the
// full canvas to a box makes icons that are meant to read as the same size
// come out wildly different. Fitting to the measured content bounds instead
// makes every icon read as the same true size regardless of its own padding.
const SHIELD_SRC         = { l: 105, t: 45,  r: 921,  b: 987 };
const COINS_SRC          = { l: 290, t: 260, r: 1033, b: 876 };
const STATE_DONE_SRC     = { l: 207, t: 168, r: 1049, b: 1069 };
const STATE_PROGRESS_SRC = { l: 275, t: 95, r: 1127, b: 1023 };
const STATE_LOCKED_SRC   = { l: 458, t: 180, r: 932,  b: 901 };

/** Places `key` scaled so its measured content bounds (`src`) are `h` tall,
 * centered at (cx, cy) — the icon's true visual center lands on (cx, cy)
 * regardless of how much transparent padding its source canvas carries. */
function iconAt(
  scene: Phaser.Scene, cx: number, cy: number, key: string,
  src: { l: number; t: number; r: number; b: number }, h: number,
): Phaser.GameObjects.Image {
  const scale = h / (src.b - src.t);
  const w = (src.r - src.l) * scale;
  const left = cx - w / 2, top = cy - h / 2;
  return scene.add.image(left - src.l * scale, top - src.t * scale, key).setOrigin(0, 0).setScale(scale);
}

/** Visible width `iconAt` would draw for `src` at target height `h`, without
 * having to create the image first — lets column layout be computed before
 * icons are placed. */
function iconVisibleWidth(h: number, src: { l: number; t: number; r: number; b: number }): number {
  const scale = h / (src.b - src.t);
  return (src.r - src.l) * scale;
}

type LevelState = "complete" | "active" | "locked";

/** Campaign quest board. Quest requirements reuse the same saved statistics
 * and achievement unlocks as the rest of the game, so no progress is split. */
export class QuestScene extends Phaser.Scene {
  private list!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private scrollBar?: Phaser.GameObjects.Rectangle;
  private scrollBarTrack?: Phaser.GameObjects.Rectangle;

  /** List y of each level's heading, so map flags can scroll to their block. */
  private levelAnchors = new Map<number, number>();

  /** Bound wheel handler so we can detach it cleanly on shutdown. */
  private wheelHandler?: (
    ptr: Phaser.Input.Pointer,
    over: Phaser.GameObjects.GameObject[],
    dx: number, dy: number
  ) => void;

  constructor() { super("QuestScene"); }

  create(): void {
    const lifecycle = beginSceneLifecycle(this);
    quests.reconcile();
    this.scrollY = 0;
    this.levelAnchors.clear();

    addMenuBackdrop(this, { darkness: 0.76 });
    addContentPanel(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, "iron", 0.97);
    addCastleBackdrop(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, { veil: "dark" });
    this.addSidebar();
    this.addJourneyMap();
    addMenuHeader(this, {
      title: "QUEST BOARD",
      subtitle: "Take an oath. Earn your place in the chronicle.",
      onBack: () => goTo(this, "MenuScene"),
    });
    addQuitButton(this);
    lifecycle.key("keydown-ESC", () => goTo(this, "MenuScene"));
    lifecycle.key("keydown-UP", () => this.applyScroll(-CARD_STRIDE));
    lifecycle.key("keydown-DOWN", () => this.applyScroll(CARD_STRIDE));
    lifecycle.key("keydown-PAGEUP", () => this.applyScroll(-LIST_H * 0.8));
    lifecycle.key("keydown-PAGEDOWN", () => this.applyScroll(LIST_H * 0.8));

    this.addCounter();
    this.buildList();
    this.addFooter();

    this.wheelHandler = (_p, _o, _dx, dy) => this.applyScroll(dy * 1.2);
    this.input.on("wheel", this.wheelHandler);
    lifecycle.add(() => {
      if (this.wheelHandler) {
        this.input.off("wheel", this.wheelHandler);
        this.wheelHandler = undefined;
      }
    });
  }

  // ── Level gating ──────────────────────────────────────────────────────────

  private questsFor(level: number): QuestDef[] {
    return QUEST_DEFS.filter(q => q.level === level);
  }

  private isLevelComplete(level: number): boolean {
    return isQuestLevelComplete(level, save.get());
  }

  /** A level opens once every quest in the level before it is completed. */
  private isLevelUnlocked(level: number): boolean {
    return isQuestLevelUnlocked(level, save.get());
  }

  private levelState(level: number): LevelState {
    if (!this.isLevelUnlocked(level)) return "locked";
    return this.isLevelComplete(level) ? "complete" : "active";
  }

  // ── Left column ───────────────────────────────────────────────────────────

  /** Painted map background clipped to the column bounds, darkened slightly.
   * No frame — the painted art carries its own edge. */
  private addSidebar(): void {
    this.add.rectangle(SIDEBAR_X, SIDEBAR_Y, SIDEBAR_W, SIDEBAR_H, THEME.hex.ironDeep)
      .setOrigin(0, 0);
    if (this.textures.exists("bg-quest-sidebar")) {
      const bg = this.add.image(SIDEBAR_X + SIDEBAR_W / 2, SIDEBAR_Y + SIDEBAR_H / 2, "bg-quest-sidebar");
      const cover = Math.max(SIDEBAR_W / bg.width, SIDEBAR_H / bg.height);
      bg.setScale(cover);
      const maskShape = this.add.graphics().setVisible(false);
      maskShape.fillStyle(0xffffff);
      maskShape.fillRect(SIDEBAR_X, SIDEBAR_Y, SIDEBAR_W, SIDEBAR_H);
      bg.setMask(maskShape.createGeometryMask());
    }
    this.add.rectangle(SIDEBAR_X, SIDEBAR_Y, SIDEBAR_W, SIDEBAR_H, 0x0d0803, 0.22).setOrigin(0, 0);
  }

  /** One banner per level down the painted map. Colour and inset icon carry
   * the level's state; unlocked banners scroll the list to their quests. */
  private addJourneyMap(): void {
    // Zigzag across the column so the trail winds like a route on the map.
    const LANE = [0.32, 0.755, 0.30, 0.62];
    // Pulls the LEVEL 3 banner up toward LEVEL 2, so the leg below it
    // sweeps out into one long, elongated hook instead of a short even hop.
    const RISE = [0, 0, 0.407, 0];
    const top = SIDEBAR_Y + 18;
    const stride = (SIDEBAR_H - 36 - FLAG_H) / Math.max(1, QUEST_LEVELS.length - 1);

    const anchors = QUEST_LEVELS.map((level, i) => ({
      level,
      cx: SIDEBAR_X + SIDEBAR_W * LANE[i % LANE.length],
      top: top + i * stride - RISE[i % RISE.length] * stride * 0.5,
    }));

    // Trails first so each dashed leg tucks in behind the banners it joins.
    for (let i = 0; i < anchors.length - 1; i++) {
      this.addTrail(anchors[i], anchors[i + 1]);
    }
    for (const anchor of anchors) {
      this.addFlag(anchor.level.id, anchor.level.icon, anchor.cx, anchor.top);
    }
  }

  /** Dashed route from one banner's pennant tip to the next one's plate,
   * bowed toward the lane it's heading into like a hand-drawn campaign
   * route, and beaded with a few glowing waypoints along the way. */
  private addTrail(from: { cx: number; top: number }, to: { cx: number; top: number }): void {
    const start = new Phaser.Math.Vector2(from.cx, from.top + FLAG_H * 0.95);
    const end = new Phaser.Math.Vector2(to.cx, to.top + FLAG_H * 0.06);
    // Vertical tangents at both ends (control points share their anchor's x)
    // bow the trail into a flowing S between lanes instead of cutting a
    // near-straight diagonal, so the beaded dots hug a proper curve.
    const midY = (start.y + end.y) / 2;
    const control1 = new Phaser.Math.Vector2(start.x, midY);
    const control2 = new Phaser.Math.Vector2(end.x, midY);
    const curve = new Phaser.Curves.CubicBezier(start, control1, control2, end);

    const dashCount = Math.max(6, Math.round(curve.getLength() / 10));
    const dashes = this.add.graphics();
    dashes.fillStyle(0xf3e6c4, 0.75);
    for (let i = 0; i <= dashCount; i++) {
      const p = curve.getPoint(i / dashCount);
      dashes.fillCircle(p.x, p.y, 1.5);
    }

    for (const t of [0.22, 0.5, 0.78]) {
      const p = curve.getPoint(t);
      this.addTrailBead(p.x, p.y, t === 0.5 ? 1 : 0.8);
    }
  }

  /** Small layered glow marking a waypoint along the journey trail. */
  private addTrailBead(x: number, y: number, scale: number): void {
    const bead = this.add.graphics();
    bead.fillStyle(0xffe9a8, 0.22);
    bead.fillCircle(x, y, 6 * scale);
    bead.fillStyle(0xfff6d8, 0.55);
    bead.fillCircle(x, y, 3 * scale);
    bead.fillStyle(0xfffdf0, 0.95);
    bead.fillCircle(x, y, 1.3 * scale);
  }

  private addFlag(level: number, levelIcon: string, cx: number, top: number): void {
    const state = this.levelState(level);
    const texture = state === "complete" ? "flag-quest-green"
      : state === "active" ? "flag-quest-blue"
      : "flag-quest-brown";
    const iconKey = state === "complete" ? "icon-quest-check"
      : state === "active" ? levelIcon
      : "icon-quest-lock";

    const flag = this.add.image(cx, top, texture)
      .setOrigin(FLAG_ORIGIN_X, 0)
      .setDisplaySize(FLAG_W, FLAG_H)
      .setAlpha(state === "locked" ? 0.72 : 1);

    this.add.text(cx, top + FLAG_H * FLAG_PLATE_CY - 2, `LEVEL ${level}`, {
      fontFamily: THEME.fonts.display, fontSize: "13px", fontStyle: "600",
      color: state === "complete" ? THEME.colors.gold : THEME.colors.ironDeep,
    }).setOrigin(0.5);

    if (this.textures.exists(iconKey)) {
      fitIcon(this.add.image(cx, top + FLAG_H * FLAG_ICON_CY, iconKey), FLAG_ICON_SIZE)
        .setAlpha(state === "locked" ? 0.85 : 1);
    }

    if (state === "locked") return;
    flag.setInteractive({ useHandCursor: true })
      .on("pointerover", () => flag.setTint(0xfff0c0))
      .on("pointerout", () => flag.clearTint())
      .on("pointerdown", () => this.scrollToLevel(level));
  }

  // ── Right column ──────────────────────────────────────────────────────────

  private addCounter(): void {
    const completed = save.get().quests.completed;
    const done = QUEST_DEFS.filter(q => !!completed[q.id]).length;
    this.add.text(CARD_X + CARD_W, 128, `${done} / ${QUEST_DEFS.length} COMPLETED`, {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.gold,
    }).setOrigin(1, 0.5);
    this.add.text(REWARD_COL_X + REWARD_COL_W / 2, 128, "REWARDS", {
      fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5, 0.5);
  }

  /** Every quest in the game, grouped by level. Levels past the player's
   * progress stay visible but locked rather than being hidden. */
  private buildList(): void {
    const maskShape = this.add.graphics().setVisible(false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(CARD_X - 14, LIST_TOP, CARD_W + 28, LIST_H);
    this.list = this.add.container(0, 0);
    this.list.setMask(maskShape.createGeometryMask());

    let y = LIST_TOP;
    for (const level of QUEST_LEVELS) {
      const defs = this.questsFor(level.id);
      if (defs.length === 0) continue;
      this.levelAnchors.set(level.id, y);
      this.addLevelHeading(level.id, level.name, y);
      y += HEADING_H;
      const state = this.levelState(level.id);
      for (const quest of defs) {
        this.addQuestCard(quest, y, state === "locked" ? level.id : null);
        y += CARD_STRIDE;
      }
      y += 6;
    }

    this.maxScroll = Math.max(0, y - LIST_TOP - LIST_H);
    this.rebuildScrollBar();
  }

  private addLevelHeading(level: number, name: string, y: number): void {
    const state = this.levelState(level);
    const completed = save.get().quests.completed;
    const defs = this.questsFor(level);
    const done = defs.filter(q => !!completed[q.id]).length;

    const title = this.add.text(CARD_X + 4, y + HEADING_H / 2, `LEVEL ${level}  —  ${name.toUpperCase()}`, {
      fontFamily: THEME.fonts.display, fontSize: "14px",
      color: state === "locked" ? THEME.colors.lock : THEME.colors.gold,
    }).setOrigin(0, 0.5);

    const status = this.add.text(CARD_X + CARD_W - 4, y + HEADING_H / 2,
      state === "locked" ? "LOCKED" : `${done} / ${defs.length}`, {
        fontFamily: THEME.fonts.body, fontSize: "13px",
        color: state === "locked" ? THEME.colors.lock : THEME.colors.parchmentDim,
      }).setOrigin(1, 0.5);

    const rule = this.add.rectangle(
      title.x + title.width + 12, y + HEADING_H / 2,
      Math.max(0, status.x - status.width - 12 - (title.x + title.width + 12)), 1,
      THEME.hex.ironEdge, state === "locked" ? 0.25 : 0.45,
    ).setOrigin(0, 0.5);

    this.list.add([title, rule, status]);
  }

  /** `lockedBehind` is the level id whose predecessor is unfinished, or null
   * when the quest is playable. */
  private addQuestCard(quest: QuestDef, y: number, lockedBehind: number | null): void {
    const data = save.get();
    const done = !!data.quests.completed[quest.id];
    const locked = lockedBehind !== null;
    const cy = y + CARD_H / 2;

    // In-progress cards get a bright parchment plate instead of the dark iron
    // fill, so an active quest stands out from the completed/locked ones.
    const active = !locked && !done;
    const plate = this.add.rectangle(
      CARD_X + CARD_W / 2, cy, CARD_W - 6, CARD_H - 6,
      active ? 0xe9d9b4 : THEME.hex.ironDeep, locked ? 0.62 : 1,
    );
    this.list.add(plate);
    // Only done/locked cards get the gold frame — an in-progress card's
    // bright plate stands on its own without one.
    if (!active) {
      this.list.add(medievalBorder(this, CARD_X + CARD_W / 2, cy, CARD_W, CARD_H)
        .setAlpha(locked ? 0.45 : 1));
    }

    const level = QUEST_LEVELS.find(l => l.id === quest.level);
    const markerKey = locked ? "icon-quest-lock-filled" : done ? "icon-quest-check" : (level?.icon ?? "icon-quest-tower");
    if (this.textures.exists(markerKey)) {
      this.list.add(fitIcon(this.add.image(CARD_X + 36, cy, markerKey), 40)
        .setAlpha(locked ? 0.5 : 1));
    }

    const textX = CARD_X + 70;
    const titleColor = locked ? THEME.colors.lock : done ? THEME.colors.gold : THEME.colors.ironDeep;
    const title = this.add.text(textX, y + 14, quest.name, {
      fontFamily: THEME.fonts.display, fontSize: "19px", color: titleColor,
      wordWrap: { width: DESC_RIGHT - textX },
    });
    const desc = this.add.text(textX, y + 41, quest.desc, {
      fontFamily: THEME.fonts.body, fontSize: "15px",
      color: locked ? THEME.colors.lock : done ? THEME.colors.parchmentDim : THEME.colors.ironDeep,
      wordWrap: { width: DESC_RIGHT - textX },
    });
    this.list.add([title, desc]);

    // Progress bar: only for quests with a numeric target. While locked, this
    // reads 0 — stats earned before the level unlocks don't count toward it.
    if (quest.target) {
      const progress = done ? quest.target : locked ? 0 : questProgress(quest, data);
      const barY = y + 70;
      const count = this.add.text(DESC_RIGHT, barY,
        `${progress.toLocaleString()} / ${quest.target.toLocaleString()}`, {
          fontFamily: THEME.fonts.body, fontSize: "16px",
          color: locked ? THEME.colors.lock : done ? THEME.colors.parchmentDim : THEME.colors.ironDeep,
        }).setOrigin(1, 0.5);
      // Reserved column halved toward the text's own width, so the blank
      // gap between the bar and the count is half of what a fixed 66px
      // reservation would leave.
      const countW = (66 + count.width) / 2;
      const barW = Math.max(40, (DESC_RIGHT - textX) - countW);
      const pct = Math.min(1, progress / quest.target);
      const bar = this.add.rectangle(textX, barY, barW, 12, THEME.hex.ironDeep, 0)
        .setOrigin(0, 0.5).setStrokeStyle(1, 0x7c7a69, locked ? 0.5 : 1);
      const fillColor = active ? 0x4482a9 : done ? 0x475a1c : THEME.hex.goldDim;
      const fill = this.add.rectangle(textX + 1, barY, Math.max(2, (barW - 2) * pct), 9, fillColor)
        .setOrigin(0, 0.5).setAlpha(locked ? 0.5 : 1);
      this.list.add([bar, fill, count]);
    }

    this.addRewardColumn(quest, cy, locked, active);
    this.addStateColumn(cy, done, locked);

    // In-progress cards get thin dividers between the description, reward,
    // and state columns, matching the progress bar's own outline color.
    if (active) {
      const dividerH = CARD_H - 20;
      this.list.add([
        this.add.rectangle(REWARD_COL_X - COL_GAP / 2, cy, 1, dividerH, 0x7c7a69),
        this.add.rectangle(STATE_COL_X - COL_GAP / 2, cy, 1, dividerH, 0x7c7a69),
      ]);
    }
  }

  /** Shield+XP always; coin-stack+SP only when the quest grants skill points.
   * Both rows share one icon column and one text column (so the icons and
   * numbers stay aligned under each other), but that shared column pair is
   * sized to the wider row and centered in the reward column as a block,
   * rather than pinned to a fixed left offset. */
  private addRewardColumn(quest: QuestDef, cy: number, locked: boolean, active: boolean): void {
    const hasSp = !!quest.reward.skillPoints;
    const color = locked ? THEME.colors.lock : active ? THEME.colors.ironDeep : THEME.colors.gold;
    const rowY: [number, number] = hasSp ? [cy - 15, cy + 15] : [cy, cy];
    const ICON_H = 26;
    const ICON_TEXT_GAP = 8;

    const xpNum = this.add.text(0, rowY[0], `+${quest.reward.xp}`, {
      fontFamily: THEME.fonts.body, fontSize: "19px", fontStyle: "500", color,
    }).setOrigin(0, 0.5);
    const xpUnit = this.add.text(0, rowY[0], " XP", {
      fontFamily: THEME.fonts.body, fontSize: "15px", fontStyle: "500", color,
    }).setOrigin(0, 0.5);

    let spNum: Phaser.GameObjects.Text | undefined;
    let spUnit: Phaser.GameObjects.Text | undefined;
    if (hasSp) {
      spNum = this.add.text(0, rowY[1], `+${quest.reward.skillPoints}`, {
        fontFamily: THEME.fonts.body, fontSize: "19px", fontStyle: "500", color,
      }).setOrigin(0, 0.5);
      spUnit = this.add.text(0, rowY[1], " SP", {
        fontFamily: THEME.fonts.body, fontSize: "15px", fontStyle: "500", color,
      }).setOrigin(0, 0.5);
    }

    const iconSlot = Math.max(
      iconVisibleWidth(ICON_H, SHIELD_SRC),
      hasSp ? iconVisibleWidth(ICON_H, COINS_SRC) : 0,
    );
    const textW = Math.max(
      xpNum.width + xpUnit.width,
      hasSp ? spNum!.width + spUnit!.width : 0,
    );
    const blockW = iconSlot + ICON_TEXT_GAP + textW;
    const blockLeft = REWARD_COL_X + (REWARD_COL_W - blockW) / 2;
    const iconX = blockLeft + iconSlot / 2;
    const textX = blockLeft + iconSlot + ICON_TEXT_GAP;

    const shield = iconAt(this, iconX, rowY[0], "icon-shield", SHIELD_SRC, ICON_H).setAlpha(locked ? 0.45 : 1);
    xpNum.setX(textX);
    xpUnit.setX(textX + xpNum.width);
    this.list.add([shield, xpNum, xpUnit]);

    if (hasSp) {
      const coins = iconAt(this, iconX, rowY[1], "icon-quest-coins", COINS_SRC, ICON_H).setAlpha(locked ? 0.45 : 1);
      spNum!.setX(textX);
      spUnit!.setX(textX + spNum!.width);
      this.list.add([coins, spNum!, spUnit!]);
    }
  }

  /** Icon + caption showing whether the quest is done, in progress, or
   * still locked behind an earlier level. */
  private addStateColumn(cy: number, done: boolean, locked: boolean): void {
    // Center between the reward/state divider and the card's right edge,
    // not just within STATE_COL_W — the hard right edge (flush with the
    // card border) carries less visual margin than the open gap on the
    // left, so centering on STATE_COL_W alone reads as pulled right.
    const cx = (STATE_COL_X - COL_GAP / 2 + CARD_X + CARD_W) / 2;
    const iconKey = done ? "icon-quest-state-done" : locked ? "icon-quest-state-locked" : "icon-quest-state-progress";
    const caption = done ? "COMPLETED" : locked ? "LOCKED" : "IN PROGRESS";
    const color = done ? THEME.colors.gold : locked ? THEME.colors.lock : THEME.colors.ironDeep;
    const stateSrc = done ? STATE_DONE_SRC : locked ? STATE_LOCKED_SRC : STATE_PROGRESS_SRC;
    const iconH = CARD_H * 0.45;
    const GAP_ICON_CAPTION = 6;

    // Measure the text block up front (fontSize alone doesn't tell us the
    // rendered height) so it can be vertically centered with the top margin
    // (card top → icon top) matching the bottom margin (caption → card bottom).
    const capText = this.add.text(cx, 0, caption, {
      fontFamily: THEME.fonts.display, fontSize: `${CARD_H * 0.12}px`, fontStyle: "600", color,
    }).setOrigin(0.5, 0);

    const blockH = iconH + GAP_ICON_CAPTION + capText.height;
    const margin = (CARD_H - blockH) / 2;
    const top = cy - CARD_H / 2 + margin;

    const iconY = top + iconH / 2;
    const capY = top + iconH + GAP_ICON_CAPTION;
    capText.setY(capY);

    if (this.textures.exists(iconKey)) {
      const drawH = locked ? iconH * 0.95 : iconH;
      this.list.add(iconAt(this, cx, iconY, iconKey, stateSrc, drawH).setAlpha(locked ? 0.75 : 1));
    }
    this.list.add(capText);
  }

  private addFooter(): void {
    // Both footer actions live in the main body column, side by side, rather
    // than splitting one into the map sidebar.
    const BTN_W = 270, BTN_GAP = 20;
    const pairW = BTN_W * 2 + BTN_GAP;
    const startX = CARD_X + (CARD_W - pairW) / 2;

    const play = button(this, startX + BTN_W / 2, GAME_HEIGHT - 58, BTN_W, 44, "PLAY CAMPAIGN", {
      variant: "parchment", display: true, fontSize: 16, color: THEME.colors.iron,
    });
    (play.getData("bg") as Phaser.GameObjects.NineSlice).on("pointerdown", () => goTo(this, "MapSelectScene"));
    const achievements = button(this, startX + BTN_W + BTN_GAP + BTN_W / 2, GAME_HEIGHT - 58, BTN_W, 44, "VIEW ACHIEVEMENTS", {
      variant: "iron", display: true, fontSize: 16, color: THEME.colors.gold,
    });
    (achievements.getData("bg") as Phaser.GameObjects.NineSlice).on("pointerdown", () => goTo(this, "AchievementsScene"));
  }

  // ── Scrolling ─────────────────────────────────────────────────────────────

  private scrollToLevel(level: number): void {
    const anchor = this.levelAnchors.get(level);
    if (anchor === undefined) return;
    this.applyScroll(anchor - LIST_TOP - this.scrollY);
  }

  private applyScroll(delta: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    this.list.y = -this.scrollY;
    this.updateScrollBar();
  }

  private rebuildScrollBar(): void {
    this.scrollBar?.destroy();      this.scrollBar = undefined;
    this.scrollBarTrack?.destroy(); this.scrollBarTrack = undefined;
    if (this.maxScroll <= 0) return;

    const barX = GAME_WIDTH - 30;
    this.scrollBarTrack = this.add.rectangle(barX, LIST_TOP, 6, LIST_H, THEME.hex.iron, 0.6)
      .setOrigin(0.5, 0).setStrokeStyle(1, THEME.hex.ironEdge, 0.4);
    const thumbH = Math.max(40, LIST_H * (LIST_H / (LIST_H + this.maxScroll)));
    this.scrollBar = this.add.rectangle(barX, LIST_TOP, 6, thumbH, THEME.hex.goldDim, 0.85)
      .setOrigin(0.5, 0);
    this.updateScrollBar();
  }

  private updateScrollBar(): void {
    if (!this.scrollBar || this.maxScroll <= 0) return;
    const pct = this.scrollY / this.maxScroll;
    this.scrollBar.y = LIST_TOP + pct * (LIST_H - this.scrollBar.height);
  }
}
